import { cn } from '@/shared/helpers/classnames'

import { type FC, type PropsWithChildren } from 'react'
import { NavLink } from 'react-router-dom'

import { useCloseOnMobile } from './useCloseOnMobile'

type SideMenuLinkProps = PropsWithChildren<{
  to?: string
  onClick?: () => void
  end?: boolean
  baseClassName: string
  trackActive?: boolean
  /** Forces the active class regardless of the NavLink match (OR-ed with the router `isActive`). */
  active?: boolean
}>

/**
 * Internal primitive — the to-vs-onClick switch shared by SideMenu.Item,
 * SideMenu.Action(secondary), and SideMenu.SubItem.
 *
 * - With `to`: renders a `<NavLink>` that auto-closes the mobile drawer on
 *   navigation. When `trackActive` is true, appends `${baseClassName}-active`
 *   on the active route.
 * - Without `to`: renders a `<button type="button">` that calls `onClick`
 *   after `closeOnMobile`.
 *
 * The visual content (icon column + label, or label only) is provided by
 * the caller via `children` so the same primitive serves both rail rows
 * and indented SubItems.
 */
export const SideMenuLink: FC<SideMenuLinkProps> = ({
  to,
  onClick,
  end,
  baseClassName,
  trackActive = false,
  active = false,
  children,
}) => {
  const closeOnMobile = useCloseOnMobile()

  if (to) {
    return (
      <NavLink
        to={to}
        end={end}
        onClick={closeOnMobile}
        className={
          trackActive
            ? ({ isActive }) => cn(baseClassName, (isActive || active) && `${baseClassName}-active`)
            : baseClassName
        }
      >
        {children}
      </NavLink>
    )
  }

  const handleClick = () => {
    closeOnMobile()
    onClick?.()
  }

  return (
    <button type="button" className={baseClassName} onClick={handleClick}>
      {children}
    </button>
  )
}
